import React, { useEffect, useRef, useState } from 'react';
import {
    Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, IconButton, TextField,
    Box,
    Select,
    FormControl,
    MenuItem,
    Checkbox,
    ListItemText,
    Autocomplete,
    FormHelperText
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';

import HistoryIcon from '@mui/icons-material/History';
import InfoIcon from '@mui/icons-material/Info';
import { Tooltip } from '@mui/material';
import api from '../../services/api';

const TableField = ({ field, onChange, errors, readOnly, formData, onFocus, isFocused, reloadData }) => {

    const [headers, setHeaders] = useState(field.tableHeaders ? { [field.name]: field.tableHeaders } : {});
    const [focusedCell, setFocusedCell] = useState({ row: null, col: null });

    const [dependentOptions, setDependentOptions] = useState({});
    const [sectionInputValues, setSectionInputValues] = useState({}); // Track input values for section filters

    useEffect(() => {
        setHeaders(field.tableHeaders ? { [field.name]: field.tableHeaders } : {});
    }, [field]);

    const [rows, setRows] = useState(() => {
        const data = formData?.[field?.name];
        if (!data) return [{}];
        
        if (typeof data === "string") {
            try {
                return JSON.parse(data);
            } catch (err) {
                console.error("Invalid JSON in formData:", err);
                return [{}];
            }
        }

        return data;
    });

    const initialized = useRef(false);

    useEffect(() => {
        const data = formData?.[field?.name];
        
        
        if (initialized.current && data && (
            (typeof data === 'string' && data !== '[]' && data !== '{}') ||
            (Array.isArray(data) && data.length > 0) ||
            (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length > 0)
        )) {
            
            let existingRows = [];
            if (typeof data === "string") {
                try {
                    existingRows = JSON.parse(data);
                } catch (err) {
                    existingRows = [{}];
                }
            } else if (Array.isArray(data)) {
                existingRows = data;
            } else {
                existingRows = [data];
            }
            
            setRows(existingRows);
            return;
        }

        let newRows = [];

        if (!data || Array.isArray(data) && data.length === 0) {
            const defaultRow = {};

            field?.tableHeaders?.forEach(header => {
                const defaultOpt = header?.fieldType?.options?.find(opt => opt.defaultValue === true);
                if (defaultOpt) {
                    defaultRow[header.header] = header.fieldType?.type === 'multi_select' ? [defaultOpt.code] : defaultOpt.code;
                }
            });

            newRows = [defaultRow];
        } else if (typeof data === "string") {
            try {
                initialized.current = true;
                newRows = JSON.parse(data);
            } catch (err) {
                newRows = [{}];
            }
        } else {
            initialized.current = true;
            newRows = data;
        }

        if(formData && headers?.[field?.name]){
            headers?.[field?.name].map((header)=>{

                if(header?.fieldType?.is_dependent === "true" && header?.fieldType?.dependent_table){
                    const dependentTable = headers?.[field?.name]?.find((t) => Array.isArray(header?.fieldType?.dependent_table) && header.fieldType.dependent_table.includes(t.fieldType.table));

                    if(dependentTable){
                        newRows.forEach((row, rowIndex) => {
                            const rowData = row[dependentTable.header];
                            if (rowData) {
                                fetchDependentOptions(dependentTable, rowData, rowIndex);
                            }
                        });
                    }
                }

            });
        }

        setRows(newRows);
        onChange(field, newRows);
    }, [formData, reloadData, field]);    

    const handleCellChange = (rowIndex, key, value) => {
        const updatedRows = [...rows];
        const isValueEmpty = (val) => {
            if (val === null || val === undefined || val === '') return true;
            if (Array.isArray(val) && val.length === 0) return true;
            if (typeof val === 'object' && !Array.isArray(val) && Object.keys(val).length === 0) return true;
            return false;
        };

        if (isValueEmpty(value)) {
            const { [key]: _, ...rest } = updatedRows[rowIndex];
            updatedRows[rowIndex] = rest;
        } else {
            updatedRows[rowIndex] = {
                ...updatedRows[rowIndex],
                [key]: value
            };
        }

        setRows(updatedRows);
        onChange && onChange(field, updatedRows);
    };

    const addRow = () => {
        const defaultRow = {};
        let defaultRowHeader = false;
        let defaultRowValue = false;

        field?.tableHeaders?.forEach(header => {
            const defaultOpt = header?.fieldType?.options?.find(opt => opt.defaultValue === true);
            if (defaultOpt) {
                defaultRowValue = header.fieldType?.type === 'multi_select' ? [defaultOpt.code] : defaultOpt.code;;
                defaultRowHeader = header;
                defaultRow[header.header] =  header.fieldType?.type === 'multi_select' ? [defaultOpt.code] : defaultOpt.code;
            }
        });

        const updatedRows = [...rows, defaultRow];
        setRows(updatedRows);
        onChange && onChange(field, updatedRows);

        if(defaultRowHeader){
            fetchDependentOptions(defaultRowHeader, defaultRowValue, updatedRows.length - 1);
        }

    };

    const deleteRow = (index) => {
        const updatedRows = rows.filter((_, i) => i !== index);

        const newDependentOptions = {};
        Object.keys(dependentOptions).forEach((key) => {
            const [rowStr, fieldName] = key.split("_");
            const rowIndex = parseInt(rowStr, 10);

            if (rowIndex < index) {
                newDependentOptions[key] = dependentOptions[key];
            } else if (rowIndex > index) {
                const newKey = `${rowIndex - 1}_${fieldName}`;
                newDependentOptions[newKey] = dependentOptions[key];
            }
        });

        setRows(updatedRows);
        setDependentOptions(newDependentOptions);
        onChange && onChange(field, updatedRows);
    };

    useEffect(() => {
        const fetchAllOptions = async () => {
            const updatedHeaders = [...headers?.[field?.name]];

            for (let i = 0; i < updatedHeaders.length; i++) {
                const header = updatedHeaders[i];
                if (header?.fieldType?.api) {

                    if(header?.fieldType?.is_dependent === "true" && header?.fieldType?.dependent_table){
                        updatedHeaders[i] = {
                            ...header,
                            fieldType: {
                                ...header.fieldType,
                                options: []
                            }
                        };
                        continue;
                    }

                    try {
                        const payload = {
                            table_name: header?.fieldType?.table
                        };

                        const response = await api.post(header?.fieldType?.api, payload);
                        const { data } = response;

                        var updatedOptions = []

                        if(header?.fieldType?.api === "/templateData/getTemplateData"){
                            updatedOptions = data.map((option) => {

                                var nameKey = Object.keys(option).find(
                                    key => !['id', 'created_at', 'updated_at'].includes(key)
                                );

                                return {
                                    name: nameKey ? option[nameKey] : '',
                                    code: option.id
                                }
                            })
                        }else{
                            updatedOptions = data.map((option) => {
                                return {
                                    name: option[header?.fieldType?.table === 'users' ? 'name' : header?.fieldType?.table + '_name'],
                                    code: option[header?.fieldType?.table === 'users' ? 'user_id' : header?.fieldType?.table + '_id']
                                }
                            })
                        }
                        
                        updatedHeaders[i] = {
                            ...header,
                            fieldType: {
                                ...header.fieldType,
                                options: updatedOptions
                            }
                        };

                    } catch (error) {
                        console.error(`Error fetching for ${header?.fieldType?.api}:`, error);
                    }
                }
            }

            setHeaders((prev) => ({
                ...prev,
                [field.name]: updatedHeaders
            }));
        };

        fetchAllOptions();
    }, []);

    const fetchDependentOptions = async (header, rowData, rowIndex) => {
        const { table: tableName, forign_key } = header.fieldType;
        
        if(!tableName || !rowData || !forign_key){
            return false;
        }

        const dependentTable = headers?.[field?.name]?.find((t) => Array.isArray(t?.fieldType?.dependent_table) && t.fieldType.dependent_table.includes(tableName));

        if (!dependentTable){
            return;
        } 

        const { api: apiPath, table } = dependentTable.fieldType;
        
        // Ensure rowData is a number if it's an act_id
        const payloadValue = typeof rowData === 'string' && !isNaN(Number(rowData)) ? Number(rowData) : rowData;
        
        const payload = {
            table_name: table,
            [forign_key]: payloadValue
        }

        try {
            const response = await api.post(apiPath, payload);

            const data = response.data;

            var updatedOptions = [];

            if (apiPath === "/templateData/getTemplateData") {
                updatedOptions =  data.map(option => {
                    const nameKey = Object.keys(option).find(k => !['id', 'created_at', 'updated_at'].includes(k));
                    return {
                        name: nameKey ? option[nameKey] : '',
                        code: option.id
                    };
                });
            } else {
                updatedOptions = data.map(option => ({
                    name: option[table === 'users' ? 'name' : `${table}_name`],
                    code: option[table === 'users' ? 'user_id' : `${table}_id`]
                }));
            }

            const updatedHeaders = [...headers[field.name]];
            const depIndex = updatedHeaders.findIndex(h => h.header === dependentTable.header);

            if (depIndex !== -1) {
                updatedHeaders[depIndex] = {
                    ...updatedHeaders[depIndex],
                    fieldType: {
                        ...updatedHeaders[depIndex].fieldType,
                        options: updatedOptions
                    }
                };

                setHeaders((prev) => ({
                    ...prev,
                    [field.name]: updatedHeaders
                }));
            }

            setDependentOptions(prev => ({
                ...prev,
                [`${rowIndex}_${dependentTable.header}`]: updatedOptions
            }));

        } catch (error) {
            console.error(`Error fetching dependent options:`, error);
            return [];
        }
    };
        
    if (!headers?.[field?.name] || headers[field.name].length === 0){
        return <p style={{ color: '#333', textAlign: 'center', width: '100%', fontWeight: 500 }}>No Table Headers. </p>;
    }
    
    return (
        <Box width={'100%'} onClick={onFocus && onFocus}>
            <h4 className={`form-field-heading ${readOnly || field.disabled ? 'disabled' : ''}`}>
                <div style={{ display: 'flex', alignItems: 'center',color: Boolean(errors?.[field?.name]) ? '#F04438' : '' }}>
                    <span>
                        {field.label}
                    </span>
                    <span className="anekKannada" style={{ marginTop: '6px' }}>
                        {field.kannada ? '/ ' + field.kannada + ' ' : ''}
                    </span>
                    {field.info && (
                        <Tooltip title={field.info ? field.info : ''} arrow placement="top">
                            <InfoIcon className='infoIcon' sx={{
                                color: '#1570EF', 
                                padding: '0px 0px 0px 3px', 
                                fontSize: '20px',
                                verticalAlign: 'middle',
                                marginBottom:'3px'
                            }}/>
                        </Tooltip>
                    )}
                </div>
            </h4>
            <TableContainer component={Paper} sx={{ mt: 2, width: '100%', overflowX: 'auto', boxShadow: 'none', border: '1px solid #ddd', borderColor: '#ddd' }}>
                <Table size="small">
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{width: '50px'}}>S.No</TableCell>
                            {headers?.[field?.name].map((header, index) => (
                                <TableCell 
                                    key={index} 
                                    sx={{
                                        minWidth: header.fieldType?.width ? `${header.fieldType.width}px` : '120px',
                                        maxWidth: header.fieldType?.width ? `${header.fieldType.width}px` : '300px',
                                    }}
                                >
                                    <Tooltip title={header.header} arrow placement="top">
                                        <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', display: 'inline-block', maxWidth: '100%' }}>
                                            {header.header}
                                            {header?.fieldType?.required && (
                                                <span
                                                    style={{
                                                        padding: '0px 0px 0px 5px', 
                                                        verticalAlign: 'middle'
                                                    }} 
                                                    className='MuiFormLabel-asterisk MuiInputLabel-asterisk css-1ljffdk-MuiFormLabel-asterisk'
                                                >
                                                    {'*'}
                                                </span>
                                            )}
                                        </span>
                                    </Tooltip>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((row, rowIndex) => (
                            <TableRow key={rowIndex}>

                                <TableCell>
                                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'start'}}>
                                        { rowIndex + 1 }
                                        {
                                            rows.length > 1 &&
                                            <IconButton disabled={readOnly} onClick={() => deleteRow(rowIndex)} color="error">
                                                <DeleteIcon />
                                            </IconButton>
                                        }
                                        {rowIndex === rows.length - 1 && (
                                            <IconButton disabled={readOnly} onClick={addRow} color="primary">
                                                <AddIcon />
                                            </IconButton>
                                        )}
                                    </Box>
                                </TableCell>

                                {headers?.[field?.name].map((header, colIndex) => {

                                    let options = header.fieldType?.options || [];

                                    if(header?.fieldType?.is_dependent === "true" && header?.fieldType?.dependent_table){
                                        options = dependentOptions[`${rowIndex}_${header.header}`] || [];
                                    }

                                    return (
                                    <TableCell key={colIndex}>
                                        {

                                            (header?.fieldType?.type === 'short_text' || header?.fieldType?.type === 'text_area') && (
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={row[header.header] || ''}
                                                    disabled={readOnly}
                                                    onChange={(e) => handleCellChange(rowIndex, header.header, e.target.value)}
                                                    multiline={header?.fieldType?.type === 'text_area'}
                                                    rows={header?.fieldType?.type === 'text_area' && focusedCell.row === rowIndex && focusedCell.col === colIndex ? 6 : 1}
                                                    onFocus={() => setFocusedCell({ row: rowIndex, col: colIndex })}
                                                    onBlur={() => setFocusedCell({ row: null, col: null })}
                                                    error={header?.fieldType?.required && Boolean(errors?.[field?.name]) && !row[header.header]}
                                                />
                                            )
                                            ||
                                            header?.fieldType?.type === 'number' && (
                                                <TextField
                                                    variant="outlined"
                                                    size="small"
                                                    fullWidth
                                                    value={row[header.header] || ''}
                                                    disabled={readOnly}
                                                    onChange={(e) => {
                                                        const newValue = e.target.value;
                                                        if (newValue === '' || /^[0-9\b]+$/.test(newValue)) {
                                                            handleCellChange(rowIndex, header.header, newValue);
                                                        }
                                                    }}
                                                    inputProps={{ inputMode: 'numeric' }}
                                                    error={header?.fieldType?.required && Boolean(errors?.[field?.name]) && !row[header.header]}
                                                />
                                            ) 
                                            ||
                                            header?.fieldType?.type === 'date' && (
                                                <TextField
                                                    type="date"
                                                    size="small"
                                                    fullWidth
                                                    value={row[header.header] || ''}
                                                    disabled={readOnly}
                                                    onChange={(e) => handleCellChange(rowIndex, header.header, e.target.value)}
                                                    error={header?.fieldType?.required && Boolean(errors?.[field?.name]) && !row[header.header]}
                                                />
                                            )
                                            ||
                                            header?.fieldType?.type === 'single_select' && (() => {
                                                // Get input value for this specific cell
                                                const inputKey = `${rowIndex}-${header.header}`;
                                                const inputValue = sectionInputValues[inputKey] || "";
                                                
                                                // Filter options based on input value
                                                const allOptions = options || [];
                                                let filteredOptions = allOptions;
                                                
                                                if (inputValue && inputValue.trim() !== "") {
                                                    const searchTerm = inputValue.trim();
                                                    
                                                    filteredOptions = allOptions.filter((option) => {
                                                        const optionCodeStr = String(option.code || "").trim();
                                                        const optionNameStr = String(option.name || "").toLowerCase().trim();
                                                        const searchTermLower = searchTerm.toLowerCase();
                                                        
                                                        // For code: exact match or starts with (more precise)
                                                        const codeMatch = optionCodeStr === searchTerm || optionCodeStr.startsWith(searchTerm);
                                                        // For name: starts with or contains (but prefer starts with)
                                                        const nameMatch = optionNameStr.startsWith(searchTermLower) || optionNameStr.includes(searchTermLower);
                                                        
                                                        return codeMatch || nameMatch;
                                                    });
                                                }
                                                
                                                const selectedValue = filteredOptions.find(opt => 
                                                    String(opt.code) === String(row[header.header]) || Number(opt.code) === Number(row[header.header])
                                                ) || allOptions.find(opt => 
                                                    String(opt.code) === String(row[header.header]) || Number(opt.code) === Number(row[header.header])
                                                ) || null;
                                                
                                                return (
                                                    <FormControl fullWidth size="small" error={header?.fieldType?.required && Boolean(errors?.[field?.name]) && !row[header.header]}>
                                                        <Autocomplete
                                                            key={`single-select-autocomplete-${rowIndex}-${header.header}`}
                                                            options={filteredOptions}
                                                            value={selectedValue || null}
                                                            getOptionLabel={(option) => option.name || ""}
                                                            isOptionEqualToValue={(option, value) => {
                                                                return String(option.code) === String(value.code);
                                                            }}
                                                            onChange={(_, newValue) => {
                                                                const code = newValue?.code || null;
                                                                fetchDependentOptions(header, code, rowIndex);
                                                                handleCellChange(rowIndex, header.header, code);
                                                                
                                                                // Clear the filter input when a value is selected
                                                                setSectionInputValues(prev => ({
                                                                    ...prev,
                                                                    [inputKey]: ""
                                                                }));
                                                            }}
                                                            onInputChange={(event, newInputValue, reason) => {
                                                                // Only update on 'input' reason, not on 'clear' or 'blur'
                                                                if (reason === 'input' || reason === 'clear') {
                                                                    setSectionInputValues(prev => ({
                                                                        ...prev,
                                                                        [inputKey]: newInputValue
                                                                    }));
                                                                }
                                                            }}
                                                            disabled={readOnly}
                                                            disableListWrap
                                                            filterSelectedOptions={false}
                                                            includeInputInList={false}
                                                            clearOnBlur={false}
                                                            selectOnFocus={false}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                />
                                                            )}
                                                        />
                                                    </FormControl>
                                                );
                                            })()
                                            ||
                                            header?.fieldType?.type === 'multi_select' && (() => {
                                                // Get input value for this specific cell
                                                const inputKey = `${rowIndex}-${header.header}`;
                                                const inputValue = sectionInputValues[inputKey] || "";
                                                
                                                // Filter options based on input value
                                                const allOptions = options || [];
                                                let filteredOptions = allOptions;
                                                
                                                if (inputValue && inputValue.trim() !== "") {
                                                    const searchTerm = inputValue.trim();
                                                    
                                                    filteredOptions = allOptions.filter((option) => {
                                                        const optionCodeStr = String(option.code || "").trim();
                                                        const optionNameStr = String(option.name || "").toLowerCase().trim();
                                                        const searchTermLower = searchTerm.toLowerCase();
                                                        
                                                        // For code: exact match or starts with (more precise)
                                                        const codeMatch = optionCodeStr === searchTerm || optionCodeStr.startsWith(searchTerm);
                                                        // For name: starts with or contains (but prefer starts with)
                                                        const nameMatch = optionNameStr.startsWith(searchTermLower) || optionNameStr.includes(searchTermLower);
                                                        
                                                        return codeMatch || nameMatch;
                                                    });
                                                }
                                                
                                                return (
                                                    <FormControl fullWidth size="small" error={(header?.fieldType?.required && Boolean(errors?.[field?.name])) && (!row[header.header] || row[header.header].length === 0)}>
                                                        <Autocomplete
                                                            key={`section-autocomplete-${rowIndex}-${header.header}`}
                                                            multiple
                                                            disableCloseOnSelect
                                                            options={filteredOptions}
                                                            getOptionLabel={(option) => option.name || ""}
                                                            getOptionKey={(option, index) => {
                                                                return option.code !== undefined && option.code !== null 
                                                                    ? `section-${option.code}-${index}` 
                                                                    : `section-${index}`;
                                                            }}
                                                            isOptionEqualToValue={(option, value) => {
                                                                return String(option.code) === String(value.code);
                                                            }}
                                                            openOnFocus={false}
                                                            disableListWrap
                                                            filterSelectedOptions={false}
                                                            includeInputInList={false}
                                                            clearOnBlur={false}
                                                            selectOnFocus={false}
                                                            ListboxProps={{
                                                                style: {
                                                                    maxHeight: '300px',
                                                                }
                                                            }}
                                                            value={
                                                                (row[header.header] || []).map(code => {
                                                                    const found = options?.find(opt => {
                                                                        return String(opt.code) === String(code) || Number(opt.code) === Number(code);
                                                                    });
                                                                    return found;
                                                                }).filter(Boolean)
                                                            }
                                                            onChange={(_, newValue) => {
                                                                const codes = newValue.map(opt => opt.code);
                                                                fetchDependentOptions(header, codes, rowIndex);
                                                                handleCellChange(rowIndex, header.header, codes);
                                                                
                                                                // Clear the filter input when a value is selected
                                                                setSectionInputValues(prev => ({
                                                                    ...prev,
                                                                    [inputKey]: ""
                                                                }));
                                                            }}
                                                            onInputChange={(event, newInputValue, reason) => {
                                                                // Only update on 'input' reason, not on 'clear' or 'blur'
                                                                if (reason === 'input' || reason === 'clear') {
                                                                    setSectionInputValues(prev => ({
                                                                        ...prev,
                                                                        [inputKey]: newInputValue
                                                                    }));
                                                                }
                                                            }}
                                                            componentsProps={{
                                                                popper: {
                                                                    modifiers: [
                                                                        {
                                                                            name: 'offset',
                                                                            options: {
                                                                                offset: [0, 8],
                                                                            },
                                                                        },
                                                                    ],
                                                                },
                                                            }}
                                                            limitTags={2}
                                                            renderInput={(params) => (
                                                                <TextField
                                                                    {...params}
                                                                    variant="outlined"
                                                                    size="small"
                                                                    fullWidth
                                                                    inputProps={{
                                                                        ...params.inputProps,
                                                                        autoComplete: 'off',
                                                                    }}
                                                                />
                                                            )}
                                                            disabled={readOnly}
                                                            noOptionsText="No sections found"
                                                        />
                                                    </FormControl>
                                                );
                                            })()
                                        }
                                    </TableCell>
                                )}
                            )}
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default TableField;
